import { useState, useCallback, useRef, useEffect } from 'react';
import { ActionMenu, ActionMenuItem, useActionMenu } from 'components/ActionMenu';
import useIntegrationSettingsModal from 'hooks/useIntegrationSettingsModal';
import { Box, IconButton, Chip } from '@mui/material';
import { MoreVertical, Settings } from 'react-feather';
import { useSelector } from 'react-redux';
import { selectAuthAccount } from 'AuthModule/selectors';
import { useUpdatePhaseOrderMutation } from 'IntegrationModule/api/queries/useUpdatePhaseOrderMutation';
import { useTransitionStateMutation } from 'IntegrationModule/api/queries/useTransitionStateMutation';
import { useDeleteIntegrationMutation } from 'IntegrationModule/api/queries/useDeleteIntegrationMutation';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import OdbcCredentialCheckModal from 'IntegrationModule/components/OdbcCredentialCheckModal';
import StateTransitionConfirmModal, {
  ACTION_TO_NEW_STATE,
} from 'IntegrationModule/components/StateTransitionConfirmModal';
import EditConnectionInfoModal from 'IntegrationModule/components/EditConnectionInfoModal';
import ChangeSyncFrequencyModal from 'IntegrationModule/components/ChangeSyncFrequencyModal';
import { IntegrationState } from 'types/integration';
import noop from 'lodash/noop';

const useIntegrationOptions = () => {
  const { anchorEl, anchorPosition, isOpen, openMenu, closeMenu } = useActionMenu();
  const { handleOpenIntegrationSettingsModal } = useIntegrationSettingsModal();
  const [menuItems, setMenuItems] = useState<ActionMenuItem[]>([]);
  // Track pending menu open to avoid flushSync - menu opens after items are set
  const pendingOpenRef = useRef<HTMLElement | null>(null);

  // Open menu after items are set (avoids flushSync anti-pattern)
  useEffect(() => {
    if (pendingOpenRef.current && menuItems.length > 0) {
      openMenu(pendingOpenRef.current);
      pendingOpenRef.current = null;
    }
  }, [menuItems, openMenu]);

  const handleOpenMenu = useCallback((element: HTMLElement, items: ActionMenuItem[]) => {
    pendingOpenRef.current = element;
    setMenuItems(items);
  }, []);

  return {
    IntegrationOptionsMenu: (
      <ActionMenu
        anchorEl={anchorEl}
        anchorPosition={anchorPosition}
        open={isOpen}
        onClose={closeMenu}
        items={menuItems}
      />
    ),
    handleOpenMenu,
    handleOpenIntegrationSettingsModal,
  };
};

export default useIntegrationOptions;

export const IntegrationOptions = ({
  handleOpenIntegrationSettingsModal,
  handleOpenMenu,
  targetServiceId,
  teamId,
  state,
  isBakIntegration = false,
  syncFrequencyUpdatedOnSuccess = noop,
  showSettingButton = true,
  showOdbcChip = false,
  showBakChip = false,
  targetService,
  customDailyFetchCount,
  customDailyFetchTime,
  tenant,
  realmId,
}: Omit<ReturnType<typeof useIntegrationOptions>, 'IntegrationOptionsMenu'> & {
  targetServiceId: number;
  teamId: number;
  state?: IntegrationState;
  isBakIntegration?: boolean;
  showSettingButton?: boolean;
  showOdbcChip?: boolean;
  showBakChip?: boolean;
  targetService?: string;
  /** Current daily sync count, used to seed the Change Sync Frequency modal. */
  customDailyFetchCount?: number;
  /** Current scheduled UTC sync times, used to seed the Change Sync Frequency modal. */
  customDailyFetchTime?: string | string[];
  tenant?: string;
  realmId?: string;
  /**
   * Called after a successful sync-frequency change so redux-backed callers can
   * refetch the integration (the mutation only invalidates the react-query cache).
   */
  syncFrequencyUpdatedOnSuccess?: ({ targetServiceId }: { targetServiceId: number }) => void;
}) => {
  const account = useSelector(selectAuthAccount);
  const galaxyUserId = account?.id?.toString() || '0';
  const galaxyUserEmail = account?.email || '';

  const { updatePhaseOrder } = useUpdatePhaseOrderMutation();
  const { transitionState } = useTransitionStateMutation();
  const { deleteIntegration } = useDeleteIntegrationMutation();
  const { openModal } = useDynamicModal();

  const isAjeraIntegration = targetService?.startsWith('deltek-ajera-');

  // `state` is the source of truth for integration lifecycle (GLX-1602).
  const effectiveState = state;

  const handleOpenOdbcCredentialCheckModal = useCallback(() => {
    openModal({
      modal: (modalProps) => <OdbcCredentialCheckModal {...modalProps} />,
    });
  }, [openModal]);

  const openConfirmModal = useCallback(
    (action: 'suspend' | 'archive' | 'activate' | 'unarchive' | 'delete') => {
      openModal({
        modal: (modalProps) => (
          <StateTransitionConfirmModal
            {...modalProps}
            action={action}
            onConfirm={(reason) => {
              if (action === 'delete') {
                deleteIntegration({
                  targetServiceId,
                  galaxyUserId,
                  galaxyUserEmail,
                  reason,
                  tenant,
                  realmId,
                });
              } else {
                transitionState({
                  targetServiceId,
                  newState: ACTION_TO_NEW_STATE[action],
                  reason,
                  galaxyUserId,
                  galaxyUserEmail,
                  tenant,
                  realmId,
                });
              }
              modalProps.toggle();
            }}
          />
        ),
      });
    },
    [
      openModal,
      deleteIntegration,
      transitionState,
      targetServiceId,
      galaxyUserId,
      galaxyUserEmail,
      tenant,
      realmId,
    ],
  );

  const openEditConnectionInfoModal = useCallback(() => {
    openModal({
      modal: (modalProps) => (
        <EditConnectionInfoModal
          {...modalProps}
          targetServiceId={targetServiceId}
          teamId={teamId}
          serviceName={targetService || ''}
          isBakIntegration={isBakIntegration}
          tenant={tenant}
          realmId={realmId}
        />
      ),
    });
  }, [openModal, targetServiceId, teamId, targetService, isBakIntegration, tenant, realmId]);

  const openChangeSyncFrequencyModal = useCallback(() => {
    openModal({
      modal: (modalProps) => (
        <ChangeSyncFrequencyModal
          {...modalProps}
          mosaicTeamId={teamId}
          serviceName={targetService || ''}
          targetServiceId={targetServiceId}
          currentDailyFetchCount={customDailyFetchCount}
          currentDailyFetchTime={customDailyFetchTime}
          tenant={tenant}
          realmId={realmId}
          onUpdated={() => syncFrequencyUpdatedOnSuccess({ targetServiceId })}
        />
      ),
    });
  }, [
    openModal,
    teamId,
    targetService,
    targetServiceId,
    customDailyFetchCount,
    customDailyFetchTime,
    tenant,
    realmId,
    syncFrequencyUpdatedOnSuccess,
  ]);

  // Hide settings button for DRAFT state (not yet active)
  const shouldShowSettings = showSettingButton && effectiveState !== IntegrationState.DRAFT;

  return (
    <Box display="flex" flexDirection="row" gap={4} alignItems={'center'} marginLeft={3}>
      {showOdbcChip ? <Chip color="primary" label={'ODBC'} /> : <></>}
      {showBakChip ? <Chip color="primary" label={'.Bak Integration'} /> : <></>}
      {shouldShowSettings ? (
        <IconButton
          onClick={() => {
            handleOpenIntegrationSettingsModal({
              targetServiceId,
              teamId,
            });
          }}
          color="primary"
        >
          <Settings />
        </IconButton>
      ) : (
        <></>
      )}
      <Box
        mr={5}
        onClick={(e) => {
          e.stopPropagation();

          let items: ActionMenuItem[] = [];

          // Sync frequency edits require the service slug for the IS endpoint, so
          // only offer it when targetService is known (GLX-1609).
          const changeSyncFrequencyItem: ActionMenuItem[] = targetService
            ? [{ label: 'Change Sync Frequency', onClick: openChangeSyncFrequencyModal }]
            : [];

          switch (effectiveState) {
            case IntegrationState.DRAFT:
              items = [
                ...(targetService?.includes('hosted')
                  ? [{ label: 'Edit Connection Info', onClick: openEditConnectionInfoModal }]
                  : []),
                { label: 'Delete', onClick: () => openConfirmModal('delete') },
              ];
              break;

            case IntegrationState.ACTIVE: {
              const commonItems: ActionMenuItem[] = [
                {
                  label: 'Retroactive Phase Order',
                  onClick: () => {
                    updatePhaseOrder({ targetServiceId });
                  },
                },
              ];
              if (isAjeraIntegration) {
                commonItems.push({
                  label: 'Check ODBC Credential Validity',
                  onClick: handleOpenOdbcCredentialCheckModal,
                });
              }
              items = [
                ...commonItems,
                ...changeSyncFrequencyItem,
                { label: 'Suspend', onClick: () => openConfirmModal('suspend') },
                { label: 'Archive', onClick: () => openConfirmModal('archive') },
              ];
              break;
            }

            case IntegrationState.SUSPENDED:
              items = [
                { label: 'Activate', onClick: () => openConfirmModal('activate') },
                ...changeSyncFrequencyItem,
                { label: 'Archive', onClick: () => openConfirmModal('archive') },
              ];
              break;

            case IntegrationState.ARCHIVED:
              items = [
                { label: 'Unarchive', onClick: () => openConfirmModal('unarchive') },
                { label: 'Delete', onClick: () => openConfirmModal('delete') },
              ];
              break;

            default:
              // No known state (e.g. integration not yet loaded) — no actions.
              break;
          }

          handleOpenMenu(e.currentTarget, items);
        }}
      >
        <IconButton>
          <MoreVertical />
        </IconButton>
      </Box>
    </Box>
  );
};
